import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import RichContent from '../components/RichContent';
import { blogAPI } from '../services/api';
import type { BlogPost } from '../types';
import { canonicalUrlFor, metaDescription } from '../utils/seo';

const BlogArticle: React.FC = () => {
  const { slug } = useParams();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    blogAPI
      .getPublicPost(slug)
      .then(({ data }) => {
        if (!cancelled) {
          setPost(data.post);
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load blog article', err);
        setError('This article could not be loaded.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const canonicalPath = post?.canonical_path ?? (slug ? `/blog/${slug}` : '/blog');

  return (
    <>
      <SEO
        title={post ? `${post.title} | OppFoundry Blog` : 'OppFoundry Blog'}
        description={metaDescription(post?.subheading || post?.body_preview || 'OppFoundry blog article')}
        canonical={canonicalUrlFor(canonicalPath)}
        og={{
          title: post ? `${post.title} | OppFoundry Blog` : 'OppFoundry Blog',
          description: metaDescription(post?.subheading || post?.body_preview || 'OppFoundry blog article'),
          url: canonicalUrlFor(canonicalPath) ?? undefined,
          image: post?.display_image_url || post?.cover_image?.url,
        }}
      />
      <div className="bg-[linear-gradient(180deg,_#f8fbff_0%,_#ffffff_16%,_#ffffff_100%)]">
        <Layout className="max-w-7xl py-12 sm:py-16">
          <Link to="/blog" className="inline-flex items-center text-sm font-medium text-sky-700 hover:text-sky-800">
            Back to blog
          </Link>

          {loading ? (
            <div className="mt-8 border border-dashed border-slate-300 px-6 py-16 text-center text-slate-500">
              Loading article...
            </div>
          ) : error || !post ? (
            <div className="mt-8 border border-rose-200 bg-rose-50 px-6 py-10 text-sm text-rose-700">
              {error || 'Article not found.'}
            </div>
          ) : (
            <article className="mt-8">
              <div className="bg-[linear-gradient(90deg,transparent_0,transparent_11.5%,rgba(148,163,184,0.22)_11.5%,rgba(148,163,184,0.22)_11.65%,transparent_11.65%,transparent_43%,rgba(148,163,184,0.16)_43%,rgba(148,163,184,0.16)_43.15%,transparent_43.15%,transparent_74%,rgba(148,163,184,0.12)_74%,rgba(148,163,184,0.12)_74.15%,transparent_74.15%,transparent_100%)]">
                <div className="grid gap-10 lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-14">
                  <aside className="pt-6 lg:pt-32">
                    <div className="border border-dashed border-slate-300/80 bg-white/55 p-5">
                      <div className="flex items-center gap-4 lg:flex-col lg:items-start">
                        <div className="flex h-14 w-14 items-center justify-center rounded-full border border-slate-300 bg-white text-lg font-semibold text-slate-700">
                          {initialsFor(post.author.name)}
                        </div>
                        <div>
                          <p className="text-lg font-semibold text-slate-900">{post.author.name}</p>
                          {post.author.email && (
                            <p className="mt-1 text-sm text-slate-500">{post.author.email}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </aside>

                  <div className="min-w-0 pb-12">
                    <header className="border-b border-dashed border-slate-300/90 pb-10 pt-2">
                      <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-sky-700">
                        <span>Blog</span>
                        {post.tags.length > 0 && (
                          <>
                            <span className="text-slate-300">/</span>
                            <span className="text-slate-500">{post.tags[0]}</span>
                          </>
                        )}
                      </div>

                      <h1 className="mt-8 max-w-4xl text-4xl font-semibold tracking-tight text-slate-950 sm:text-6xl sm:leading-[1.02]">
                        {post.title}
                      </h1>

                      <div className="mt-8 border-l-2 border-sky-200 pl-4">
                        <p className="text-lg font-medium text-slate-500">
                          {formatBlogDate(post.published_at)}
                        </p>
                      </div>

                      {post.subheading && (
                        <p className="mt-8 max-w-4xl text-xl leading-9 text-slate-600">
                          {post.subheading}
                        </p>
                      )}
                    </header>

                    {post.display_image_url && (
                      <div className="border-b border-dashed border-slate-300/90 py-10">
                        <img
                          src={post.display_image_url}
                          alt={post.title}
                          className="aspect-[16/8] w-full rounded-[1.75rem] object-cover shadow-[0_24px_70px_rgba(15,23,42,0.08)]"
                        />
                      </div>
                    )}

                    <div className="border-b border-dashed border-slate-300/90 py-10">
                      <div className="flex flex-wrap gap-2">
                        {post.tags.map((tag) => (
                          <span
                            key={tag}
                            className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium uppercase tracking-[0.12em] text-slate-600"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="py-10">
                      <RichContent
                        html={post.body_html}
                        className="prose-lg prose-p:text-slate-600 prose-p:leading-9 prose-headings:text-slate-950 prose-h2:mt-16 prose-h2:border-t prose-h2:border-dashed prose-h2:border-slate-300 prose-h2:pt-8 prose-h3:mt-12"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </article>
          )}
        </Layout>
      </div>
    </>
  );
};

function formatBlogDate(value?: string | null): string {
  if (!value) return 'Draft';
  return new Date(value).toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function initialsFor(name: string): string {
  const parts = name
    .split(' ')
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(0, 2);

  if (parts.length === 0) return 'OF';
  return parts.map((part) => part[0]?.toUpperCase() ?? '').join('');
}

export default BlogArticle;
